import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money, formatErr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, SlidersHorizontal, Pencil, UserCheck } from "lucide-react";

const BLANK = { name: "", level: 1, min_amount: 0, max_amount: "", approver_user_id: "any" };

export default function ApprovalRules() {
    const [rules, setRules] = useState(null);
    const [approvers, setApprovers] = useState([]);
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(BLANK);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/approval-rules").then((r) => setRules(r.data)).catch(() => setRules([]));
    useEffect(() => {
        load();
        api.get("/approvers").then((r) => setApprovers(r.data)).catch(() => setApprovers([]));
    }, []);

    const nameOf = (id) => approvers.find((a) => a.id === id)?.name || "Unknown approver";
    const isL1 = Number(form.level) === 1;

    const openCreate = () => { setEditId(null); setForm(BLANK); setOpen(true); };
    const openEdit = (r) => {
        setEditId(r.id);
        setForm({
            name: r.name || "", level: r.level || 1,
            min_amount: r.min_amount ?? 0,
            max_amount: r.max_amount == null ? "" : r.max_amount,
            approver_user_id: r.approver_user_id || "any",
        });
        setOpen(true);
    };

    const save = async () => {
        if (!form.name) { toast.error("Name is required"); return; }
        setBusy(true);
        const level = Number(form.level);
        const payload = {
            name: form.name, level, approver_role: "approver",
            approver_user_id: level === 1 || form.approver_user_id === "any" ? null : form.approver_user_id,
            min_amount: Number(form.min_amount || 0),
            max_amount: form.max_amount === "" ? null : Number(form.max_amount),
        };
        try {
            if (editId) { await api.put(`/approval-rules/${editId}`, payload); toast.success("Rule updated"); }
            else { await api.post("/approval-rules", payload); toast.success("Rule added"); }
            setOpen(false);
            setForm(BLANK);
            load();
        } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const del = async (rid) => {
        try { await api.delete(`/approval-rules/${rid}`); toast.success("Removed"); load(); }
        catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    };

    const approverLabel = (r) => r.level === 1
        ? "Requester's initial approver"
        : (r.approver_user_id ? nameOf(r.approver_user_id) : "Any approver");

    return (
        <div className="space-y-6 pe-fade-up max-w-3xl">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald">Approval Rules</h1>
                    <p className="text-muted-foreground text-sm mt-1">Configure how requests route for approval at your school.</p>
                </div>
                <Button onClick={openCreate} className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="add-rule-button"><Plus className="h-4 w-4 mr-1" /> Add Rule</Button>
            </div>

            <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 text-brand-emerald text-xs px-4 py-3 flex items-start gap-2" data-testid="approval-info-banner">
                <UserCheck className="h-4 w-4 mt-0.5 shrink-0" />
                <span><b>Level 1</b> always routes to each requester's <b>initial approver</b> (set per person in School Settings → Users &amp; Roles). For <b>Levels 2–4</b> you choose the approver here in the rule.</span>
            </div>

            <div className="bg-white border border-border rounded-xl overflow-hidden">
                {rules === null ? (
                    <div className="grid place-items-center py-16 text-brand-emerald"><Loader2 className="h-6 w-6 animate-spin" /></div>
                ) : rules.length === 0 ? (
                    <div className="py-16 text-center text-muted-foreground"><SlidersHorizontal className="h-8 w-8 mx-auto mb-3 opacity-40" /> No rules configured.</div>
                ) : (
                    <div className="divide-y divide-border">
                        {rules.map((r) => (
                            <div key={r.id} className="flex items-center justify-between px-5 py-4" data-testid={`rule-${r.id}`}>
                                <div>
                                    <div className="font-medium text-brand-emerald flex items-center gap-2">
                                        <span className="h-6 w-6 grid place-items-center rounded-full bg-brand-emerald text-white text-xs">{r.level}</span>
                                        {r.name}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1 ml-8 font-mono">
                                        {money(r.min_amount)} – {r.max_amount == null ? "∞" : money(r.max_amount)}
                                        <span className="mx-1.5">·</span>
                                        <span className="font-sans">approver: <b className="font-medium text-slate-600">{approverLabel(r)}</b></span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Button variant="ghost" size="icon" className="text-brand-teal hover:bg-emerald-50" onClick={() => openEdit(r)} data-testid={`edit-rule-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                                    <Button variant="ghost" size="icon" className="text-rose-600 hover:bg-rose-50" onClick={() => del(r.id)} data-testid={`delete-rule-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editId ? "Edit approval rule" : "New approval rule"}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label>Rule name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Head of School (over $25,000)" className="mt-1.5" data-testid="rule-name-input" /></div>
                        <div className="grid grid-cols-3 gap-3">
                            <div><Label>Level</Label>
                                <Select value={String(form.level)} onValueChange={(v) => setForm({ ...form, level: v })}>
                                    <SelectTrigger className="mt-1.5" data-testid="rule-level-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{[1, 2, 3, 4].map((n) => <SelectItem key={n} value={String(n)}>Level {n}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div><Label>Min $</Label><Input type="number" value={form.min_amount} onChange={(e) => setForm({ ...form, min_amount: e.target.value })} className="mt-1.5" data-testid="rule-min-input" /></div>
                            <div><Label>Max $ (blank = ∞)</Label><Input type="number" value={form.max_amount} onChange={(e) => setForm({ ...form, max_amount: e.target.value })} className="mt-1.5" data-testid="rule-max-input" /></div>
                        </div>

                        {isL1 ? (
                            <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 text-brand-emerald text-xs px-3 py-2.5 flex items-start gap-2" data-testid="rule-level1-note">
                                <UserCheck className="h-4 w-4 mt-0.5 shrink-0" />
                                <span>Level 1 is always the <b>requester's initial approver</b>. Set that per person in School Settings → Users &amp; Roles.</span>
                            </div>
                        ) : (
                            <div>
                                <Label>Approver for this level</Label>
                                <Select value={String(form.approver_user_id)} onValueChange={(v) => setForm({ ...form, approver_user_id: v })}>
                                    <SelectTrigger className="mt-1.5" data-testid="rule-approver-select"><SelectValue placeholder="Choose an approver" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="any">Any approver</SelectItem>
                                        {approvers.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">This specific person must approve at this level. “Any approver” lets any user with the Approver role act.</p>
                            </div>
                        )}
                        <p className="text-xs text-muted-foreground">Requests whose total falls in this range will require approval at this level.</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button disabled={busy} onClick={save} className="bg-brand-emerald hover:bg-brand-emeraldhover" data-testid="save-rule-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editId ? "Save changes" : "Add rule")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
